import React, { PropsWithChildren, ReactNode, useState } from 'react';
import { To } from 'react-router-dom';

import {
  Box,
  Collapse,
  Container,
  Grid,
  IconButton,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';

import { VisibilityProps, useVisibility } from '../../store/use-visibility';

import { ArrowRight } from '../../atoms/icons/arrow-right';
import { HelpButton, HelpButtonProps } from '../../atoms/help-button';
import { ChevronRight } from '../../atoms/icons/chevron-right';
import { Plus } from '../../atoms/icons/plus';

type Props = PropsWithChildren<
  {
    title?: ReactNode;
    help?: HelpButtonProps;
    to?: To;
    layout?: 'none' | 'scrollable' | 'grid' | 'list';
    collapsible?: boolean;
    id?: string;
    plus?: boolean;
    sx?: SxProps;
  } & VisibilityProps
>;

export const Group = ({
  title,
  to,
  help,
  children,
  visibility,
  layout = 'none',
  collapsible,
  id,
  plus,
  sx = {},
}: Props) => {
  const [collapsed, setCollapsed] = useState(false);

  if (!useVisibility(visibility)) return null;

  const content = (
    <>
      {layout === 'none' && children}

      {layout === 'scrollable' && (
        <Box
          sx={{
            width: '100%',
            maxWidth: { xs: '100%', lg: 1200 },
            display: 'flex',
            overflow: 'auto',
            px: { xs: 2, sm: 3 },
            pb: 1,
            mx: { xs: 0, lg: 'auto' },
          }}
        >
          {children}
        </Box>
      )}

      {layout === 'grid' && (
        <Container>
          <Grid container spacing={2}>
            {children}
          </Grid>
        </Container>
      )}

      {layout === 'list' && (
        <Container>
          <Stack spacing={2}>{children}</Stack>
        </Container>
      )}
    </>
  );

  return (
    <Box sx={{ py: 1, ...sx }} id={id}>
      {/* title */}
      {title && (
        <Container
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
          className="group__title"
        >
          <Typography variant="h2">
            {title}
            {plus && <Plus sx={{ ml: 1 }} />}
          </Typography>

          {help && <HelpButton {...help} />}

          {collapsible && (
            <IconButton onClick={() => setCollapsed(!collapsed)} sx={{ ml: 1 }}>
              <ChevronRight
                sx={{
                  transform: `rotate(${collapsed ? '-' : ''}90deg)`,
                  transition: 'ease-in-out 200ms transform',
                }}
              />
            </IconButton>
          )}

          {to ? (
            <IconButton sx={{ ml: 'auto' }}>
              <ArrowRight sx={{ fontSize: 20, color: 'text.primary' }} />
            </IconButton>
          ) : (
            <Box sx={{ height: 36 }} />
          )}
        </Container>
      )}

      {/* content */}
      {collapsible ? <Collapse in={collapsed}>{content}</Collapse> : content}
    </Box>
  );
};
